/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useParams } from "react-router";
import {
  UploadedFileOutputType,
  useListUploadedFileTypesQuery,
  UserRead,
} from "../store/api/generatedApi";
import { UploadedFileType } from "components/file/upload/uploadedFileType";
import { mapLoadedUnpack } from "models/loaded";
import { useMaterials } from "contexts/search/SearchContextProvider";
import { useAppSelector } from "src/store/store";
import { useCraneImpersonation } from "components/profileSettings/useCraneImpersonation";

/**
 * Get the current tenant.  Only valid inside a `RequireTenant` component.
 */
export const useTenant = (): string => useParams().tenant!;

/**
 * Get the plan file types allowed for the current tenant.
 *
 * Only valid inside a `RequireTenant` component.
 */
export const useTenantUploadedFileTypes = (
  outputTypes?: UploadedFileOutputType[]
) => {
  const tenant = useTenant();
  const result = useListUploadedFileTypesQuery({
    tenantName: tenant,
    uploadedFileOutputTypeList: {
      items: outputTypes ?? null,
    },
  });
  return {
    ...result,
    data: result.data?.items as UploadedFileType[] | undefined,
  };
};

export const useLanguage = () =>
  useAppSelector((state) => state.settings.locale);

export const useShortLanguage = () =>
  useAppSelector(
    // NOTE: this is a bit of a bodge, and is required because the frontend uses
    // full language codes (eg. `en-US`), whereas the backend only uses their
    // shortened forms (eg. `EN`).
    (state) => state.settings.locale.split("-")[0]!.toUpperCase()
  );
export const useUser = () => {
  return useAppSelector(
    (state) =>
      ({
        uid: state.settings.userUid,
        name: state.settings.displayName,
        email: state.settings.email,
      }) as UserRead
  );
};

export const useUserUid = () => {
  const { craneToImpersonate } = useCraneImpersonation();
  return useAppSelector(
    (state) => craneToImpersonate ?? state.settings.userUid
  );
};

export const useUserDisplayName = () =>
  useAppSelector((state) => state.settings.displayName);

export const useTenantPermissions = () =>
  useAppSelector((state) => state.settings.tenantPermissions);

export const useIsAdmin = () =>
  useAppSelector((state) => state.settings.isAdmin);

export const useIsAdminPaused = () =>
  useAppSelector((state) => state.settings.isAdminPaused);

export const useUserEmail = () =>
  useAppSelector((state) => state.settings.email);

export const useHotkeysEnabled = () =>
  useAppSelector((state) => state.settings.hotkeysEnabled);

export const useManualReviewAreaHotkeyBehaviour = () =>
  useAppSelector((state) => state.settings.manualReviewAreaHotkeyBehaviour);

export const useLowMemoryVideoMode = () =>
  useAppSelector((state) => state.settings.lowMemoryVideoMode);

export const useVideoQuality = () =>
  useAppSelector((state) => state.settings.videoQuality);

export const useUsesDri = () => {
  const materials = useMaterials();

  return (
    mapLoadedUnpack(materials, ({ byIndex }) =>
      byIndex.some(
        (material) =>
          material.addition_location === "hopper" &&
          material.material_type === "iron"
      )
    ) ?? false
  );
};
